import { TenantOrder } from '../../common/TenantOrder';
import {
  BRE_CANCEL_FEE,
  BRE_COPYRIGHT_PHOTO_FEE,
  BRE_COPYRIGHT_VIDEO_FEE,
  BRE_HOLIDAY_FEE,
  BRE_HOLIDAYS,
  BRE_TRAVEL_FEES,
  BRE_WEEKEND_FEE,
  BrePrintSpeed
} from '../data';
import { addressToFull, DisplayData, TenantOrderLine } from '~/lib/model';
import Big from 'big.js';
import BreFlyersJob from '~/tenants/bre/performable/print/flyers/BreFlyersJob';
import BrePhotosJob from '~/tenants/bre/performable/photos/BrePhotosJob';

import { DateTime } from 'luxon';
import { BreOrderContext } from '~/tenants/bre/model/BreOrderContext';
import BreJob from '~/tenants/bre/model/BreJob';
import { BrePhotosRate } from '~/tenants/bre/performable/photos/BrePhotosData';
import { datetime, formalTime } from '~/lib/datettime';
import { holiday_lookup } from '~common/holidays/holidays';
import { TenantPackageConfig } from '~/tenants/common/registry';
import { deliveryFee } from '~/tenants/bre/performable/common';
import { BreOrderType } from '~/tenants/bre/model/enums';
import BreAerialJob from '~/tenants/bre/performable/aerial/BreAerialJob';
import BreCinematicJob from '~/tenants/bre/performable/cinematic/BreCinematicJob';
import BreSocialJob from '~/tenants/bre/performable/social/BreSocialJob';
import BreTwilightJob from '~/tenants/bre/performable/twilight/BreTwilightJob';

interface FeeLineOptions {
  id: string;
  date: DateTime;
  excludeWeekendFees: boolean | undefined;
}

export class BreOrder extends TenantOrder<BreOrderContext, BreJob> {
  get isFreeDelivery(): boolean {
    return this.jobs.filter((j) => j.isDelivery()).every((j) => j.isFreeDelivery());
  }

  needsDelivery(): boolean {
    return this.jobs.some((j) => j.isDelivery());
  }

  get microsite() {
    return this.jobs.some((j) => j.performable.id === 'website');
  }

  info(): DisplayData[] {
    const info = super.info();

    info.push({
      name: 'Type',
      value: this.context.metadata.type,
      invoice: true,
      provider: true,
    });

    if ('sqft' in this.context.metadata) {
      info.push({
        name: 'Sqft',
        value: this.context.metadata.sqft.toString(),
        invoice: true,
        schedule: true,
        provider: true,
      });
    }

    if (this.context.metadata.comments) {
      info.push({
        name: 'Comments',
        value: this.context.metadata.comments,
        provider: true,
      });
    }

    if ('sqft' in this.context.metadata) {
      info.push({
        name: 'Sqft',
        value: this.context.metadata.sqft.toString(),
        provider: true,
      });
    }

    if ('parkingInfo' in this.context.metadata && this.context.metadata.parkingInfo) {
      info.push({
        name: 'Parking Info',
        value: this.context.metadata.parkingInfo,
        provider: true,
      });
    }

    if ('propertyType' in this.context.metadata && this.context.metadata.propertyType) {
      info.push({
        name: 'Property Type',
        value: this.context.metadata.propertyType,
        provider: true,
      });
    }

    if (this.context.metadata.delivery) {
      info.push({
        name: 'Delivery',
        value: addressToFull(this.context.metadata.delivery),
        provider: true,
        invoice: true,
      });
    }

    if (this.context.metadata.type === BreOrderType.RESIDENTIAL) {
      info.push({
        name: 'Access',
        value: this.context.metadata.access,
        provider: true,
      });

      if (this.context.metadata.comboCode) {
        info.push({
          name: 'Combo Code',
          value: this.context.metadata.comboCode,
          provider: true,
        });
      }

      if (this.context.metadata.comboLocation) {
        info.push({
          name: 'Combo Location',
          value: this.context.metadata.comboLocation,
          provider: true,
        });
      }

      if (this.context.metadata.gateCode) {
        info.push({
          name: 'Gate Code',
          value: this.context.metadata.gateCode,
          provider: true,
        });
      }

      if (this.context.metadata.communityInfo) {
        info.push({
          name: 'Community Info',
          value: this.context.metadata.communityInfo,
          provider: true,
        });
      }

      info.push({
        name: 'Built',
        value: this.context.metadata.built.toString(),
      });

      info.push({
        name: 'Beds',
        value: this.context.metadata.beds.toString(),
      });

      info.push({
        name: 'Baths',
        value: this.context.metadata.baths.toString(),
      });

      if ('lot_sqft' in this.context.metadata && this.context.metadata.lot_sqft) {
        info.push({
          name: 'Lot Sqft',
          value: this.context.metadata.lot_sqft.toString(),
        });
      }
    }

    return info;
  }

  revenueLines(): TenantOrderLine[] {
    const { delivery, excludeTravelFees, excludeWeekendFees, type } = this.context.metadata;
    const hashRushFlyers = this.jobs.some(
      (job) => job instanceof BreFlyersJob && job.printSpeed === BrePrintSpeed.RUSH,
    );
    const hasRushPhotos = this.jobs.some((job) => job instanceof BrePhotosJob && job.metadata.rush);
    const lines = super.revenueLines();

    if (hashRushFlyers && hasRushPhotos) {
      const { RUSH } = BrePhotosRate[type];

      lines.push({
        id: 'free_rush_photos',
        description: 'Same-day flyers photo rush discount',
        discount: true,
        amount: new Big(RUSH).neg(),
      });
    }

    const pkg = this.package();

    if (pkg && !this.context.metadata.disablePackageDiscounts) {
      lines.push({
        id: 'package_discount',
        description: `${pkg.config.name} Package Discount`,
        amount: pkg.amount,
        discount: true,
      });
    }

    if (!excludeTravelFees) {
      if (!this.hasActiveAppointments && this.jobs.some((j) => j.onsite() > 0)) {
        const { zip } = this.context.address ?? {};

        if (zip) {
          const zone = BRE_TRAVEL_FEES.find((f) => f.zipcodes.includes(zip));

          const zoneFee = new Big(zone?.fee ?? '0');

          if (zoneFee.gt(0)) {
            lines.push({
              id: 'travel',
              description: 'Travel Compensation',
              amount: zoneFee,
              fee: 'travel',
            });
          }
        }

        this.context.requested.every(({ day }) => {
          const date = datetime(day, this.location.timezone);
          const feeLine = feeLineForDate({ id: 'requested', date, excludeWeekendFees });

          if (feeLine !== null) {
            lines.push(feeLine);
            return false;
          }

          return true;
        });
      }

      this.context.appointments.forEach(({ address, id, start, canceled }) => {
        const { zip } = address ?? this.context.address ?? {};
        const date = datetime(start, this.location.timezone);

        if (canceled) {
          lines.push({
            id: `cancel-${id}`,
            description: `Cancellation fee (${formalTime(date)})`,
            amount: new Big(BRE_CANCEL_FEE[canceled]),
            appointment_id: id,
          });
        } else if (zip) {
          const zone = BRE_TRAVEL_FEES.find((f) => f.zipcodes.includes(zip));
          const feeLine = feeLineForDate({ id, date, excludeWeekendFees });

          const zoneFee = new Big(zone?.fee ?? '0');

          if (zoneFee.gt(0)) {
            lines.push({
              id: `travel-${id}`,
              description: `Travel Compensation (${date.toFormat('MM/dd')})`,
              amount: zoneFee,
              fee: 'travel',
              appointment_id: id,
            });
          }

          if (feeLine !== null) {
            lines.push(feeLine);
          }
        }
      });
    }

    if (this.needsDelivery() && !this.isFreeDelivery && delivery?.time !== undefined) {
      const rate = deliveryFee(delivery.time, 'rate');

      if (rate) {
        lines.push({
          id: 'delivery_fee',
          description: `Delivery Fee (${delivery.city})`,
          amount: new Big(rate),
          fee: 'delivery',
        });
      }
    }

    if (this.context.metadata.type === BreOrderType.RENTAL) {
      const hasPhotos = this.jobs.some(
        (j) =>
          (j instanceof BreAerialJob && j.minimumPhotos > 0) ||
          (j instanceof BreCinematicJob && (j.metadata.aerial_photos || j.metadata.aerial_upgrade)) ||
          (j instanceof BrePhotosJob && j.metadata.photos > 0) ||
          (j instanceof BreTwilightJob && j.metadata.photos > 0),
      );
      const hasVideos = this.jobs.some(
        (j) =>
          (j instanceof BreAerialJob && (j.metadata.type === 'video' || j.metadata.type === 'combo')) ||
          j instanceof BreCinematicJob ||
          j instanceof BreSocialJob,
      );

      if (hasPhotos) {
        lines.push({
          id: 'photos_copyright_fee',
          description: 'Photography Copyright Fee',
          amount: new Big(BRE_COPYRIGHT_PHOTO_FEE[this.context.metadata.type]),
        });
      }

      if (hasVideos) {
        lines.push({
          id: 'video_copyright_fee',
          description: 'Video Copyright Fee',
          amount: new Big(BRE_COPYRIGHT_VIDEO_FEE[this.context.metadata.type]),
        });
      }
    }

    return lines;
  }

  expenseLines(): TenantOrderLine[] {
    const { delivery, excludeTravelFees, excludeWeekendFees } = this.context.metadata;
    const lines = super.expenseLines();

    if (!excludeTravelFees) {
      this.context.appointments.forEach(({ id, address, start, canceled }) => {
        const { zip } = address ?? this.context.address ?? {};
        const date = datetime(start, this.location.timezone);

        if (canceled) {
          lines.push({
            id: `cancel-${id}`,
            description: `Cancellation Fee (${formalTime(date)})`,
            amount: new Big(BRE_CANCEL_FEE[canceled]).times('0.65'),
            appointment_id: id,
          });
        } else if (zip) {
          const zone = BRE_TRAVEL_FEES.find(({ zipcodes }) => zipcodes.includes(zip));
          const feeLine = feeLineForDate({ id, date, excludeWeekendFees });

          const zoneFee = new Big(zone?.fee ?? '0');

          if (zoneFee.gt(0)) {
            lines.push({
              id: `travel-${id}`,
              description: `Travel Compensation (${date.toFormat('MM/dd')})`,
              amount: zoneFee,
              appointment_id: id,
              fee: 'travel',
            });
          }

          if (feeLine !== null) {
            lines.push(feeLine);
          }
        }
      });
    }

    if (this.needsDelivery() && !this.isFreeDelivery && delivery?.time !== undefined) {
      const pay = deliveryFee(delivery.time, 'pay');

      if (pay) {
        lines.push({
          id: 'delivery_fee',
          description: `Delivery Fee (${delivery.city})`,
          amount: new Big(pay),
          fee: 'delivery',
        });
      }
    }

    return lines;
  }
}

function feeLineForDate({ id, date, excludeWeekendFees }: FeeLineOptions): TenantOrderLine | null {
  const holidays = holiday_lookup(date);

  if (holidays.some((h) => BRE_HOLIDAYS.includes(h))) {
    return {
      id: `${id}-holiday`,
      description: 'Holiday Fee',
      amount: new Big(BRE_HOLIDAY_FEE),
      fee: 'holiday',
    };
  }

  if (!excludeWeekendFees && date.weekday > 5) {
    return {
      id: `${id}-weekend`,
      description: 'Weekend fee',
      amount: new Big(BRE_WEEKEND_FEE),
      fee: 'weekend',
    };
  }

  return null;
}
